/**
 * FP3-037 — Super Admin Platform Control Center.
 *
 *   /platform/dashboard
 *
 * Platform-owner lens across all organizations. Visible only to
 * `super_admin` (sidebar gates the nav entry; the backend
 * `/api/platform/overview` returns 403 to everyone else).
 *
 * Three layers stacked vertically:
 *   1. Platform totals — small cards summarizing the whole platform.
 *   2. Billing & access readiness rollup — quick health of payments.
 *   3. Organizations table — one row per org with drill-in actions.
 *
 * Drill-in: clicking "Open" navigates the super admin to
 * `/admin/dashboard?as_org={id}`. Every existing admin surface
 * stays untouched; the new `PlatformViewBanner` renders at the top
 * to signal "Super Admin View — Viewing {Org Name}" and offers a
 * one-click "Back to Platform Dashboard" link.
 *
 * "Manage admins" routes to `/admin/access?as_org={id}` so the super
 * admin gets the full Access & Invites toolbox in the chosen org's
 * context (invite Org Admin, invite Provider, view pending, resend,
 * revoke). Org admin still cannot see Access Type — that field is
 * gated in `InviteUserModal` behind `myPermission === 'super_admin'`.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Buildings,
  Users,
  GraduationCap,
  ShieldCheck,
  CreditCard,
  EnvelopeSimple,
  MagnifyingGlass,
  ArrowRight,
  Eye,
  ChartPieSlice,
  CalendarBlank,
  Gear,
  UserPlus,
  Plus,
  Info,
  Sparkle,
  EyeSlash,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
// FP3-037 correction — Super Admin onboarding modals.
import AddOrganizationModal from "@/components/platform/AddOrganizationModal";
import AddSchoolModal from "@/components/platform/AddSchoolModal";
import InviteUserModal from "@/components/admin/InviteUserModal";
// FP3-043 Phase C — Demo / test / archived classification pills.
import ClassificationBadge from "@/components/platform/ClassificationBadge";

const ACCESS_TONES = {
  membership: { bg: "#E8F0EB", fg: "#1E3F33" },
  active: { bg: "#E8F0EB", fg: "#1E3F33" },
  pilot: { bg: "#E7EDF3", fg: "#1F3F4F" },
  trial: { bg: "#E7EDF3", fg: "#1F3F4F" },
  trialing: { bg: "#E7EDF3", fg: "#1F3F4F" },
  limited: { bg: "#F5EFE3", fg: "#7A6A2C" },
  paused: { bg: "#F8EFEA", fg: "#7A3A2C" },
  canceled: { bg: "#F8EFEA", fg: "#7A3A2C" },
  past_due: { bg: "#F8EFEA", fg: "#7A3A2C" },
  not_required: { bg: "#F1F2F4", fg: "#3F4756" },
  inherited: { bg: "#F1F2F4", fg: "#6B7280" },
};

function chip(label, kind = "default") {
  const t = ACCESS_TONES[label] || ACCESS_TONES[kind] || { bg: "#F1F2F4", fg: "#3F4756" };
  return (
    <span
      className="inline-flex items-center text-[10.5px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ backgroundColor: t.bg, color: t.fg }}
    >
      {label || "—"}
    </span>
  );
}

function Stat({ label, value, testid, icon: Icon, tone = "default" }) {
  const tones = {
    default: "bg-white border-[#E5E3DB]",
    green: "bg-[#F2F7F3] border-[#D6E2D9]",
    amber: "bg-[#FDF8F3] border-[#F0E1C6]",
    blue: "bg-[#F2F6F9] border-[#D7DFE6]",
  };
  return (
    <Card className={`border rounded-2xl shadow-sm ${tones[tone] || tones.default}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-2.5">
          {Icon && (
            <div className="w-8 h-8 rounded-lg bg-[#1E3F33] flex items-center justify-center shrink-0">
              <Icon size={16} weight="duotone" className="text-white" />
            </div>
          )}
          <div className="min-w-0">
            <div
              className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold leading-tight"
              data-testid={`${testid}-label`}
            >
              {label}
            </div>
            <div
              className="text-[22px] font-semibold text-[#1F2937] heading-font leading-tight mt-0.5"
              data-testid={`${testid}-value`}
            >
              {value ?? 0}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PlatformDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  // FP3-037 correction — Onboarding modal state.
  const [addOrgOpen, setAddOrgOpen] = useState(false);
  const [addSchoolOpen, setAddSchoolOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteOrgId, setInviteOrgId] = useState("");
  const [addSchoolOrgId, setAddSchoolOrgId] = useState("");
  // FP3-037 correction — Stripe checklist panel toggle.
  const [stripeChecklistOpen, setStripeChecklistOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // FP3-043 Phase C — Persistent Super Admin "Show demo/test data"
  // toggle. Hydrated from `user.super_admin_show_hidden_data`
  // (carried on `/auth/me`), so a refresh keeps state across
  // devices.
  const [showHidden, setShowHidden] = useState(
    !!user?.super_admin_show_hidden_data
  );
  const [hiddenToggling, setHiddenToggling] = useState(false);
  // FP3-049 — "View admins" panel state. Lazily loads the org
  // detail (which includes org_admins) the first time it's opened
  // for a given org, then caches the result keyed by org_id so
  // re-opening doesn't refetch.
  const [adminsModalOrg, setAdminsModalOrg] = useState(null);
  const [adminsByOrg, setAdminsByOrg] = useState({});
  const [adminsLoading, setAdminsLoading] = useState(false);

  const openAdminsModal = async (org) => {
    setAdminsModalOrg(org);
    if (adminsByOrg[org.id]) return;
    setAdminsLoading(true);
    try {
      const r = await api.getPlatformOrgDetail(org.id);
      setAdminsByOrg((m) => ({
        ...m,
        [org.id]: r?.data?.org_admins || [],
      }));
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not load admins for this organization"
      );
      setAdminsByOrg((m) => ({ ...m, [org.id]: [] }));
    } finally {
      setAdminsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.permission_role !== "super_admin") return;
    let cancelled = false;
    setLoading(true);
    api
      .getPlatformOverview()
      .then((r) => {
        if (cancelled) return;
        setData(r.data || null);
        // Authoritative server-side toggle state — keeps the FE
        // in sync after a refresh / cross-device flip.
        if (typeof r?.data?.show_hidden_data === "boolean") {
          setShowHidden(r.data.show_hidden_data);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err?.response?.data?.detail || "Could not load platform overview"
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [user?.permission_role, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  // FP3-043 Phase C — Persistent Super Admin "Show demo/test data"
  // toggle. Server-side state is the source of truth; the call
  // refreshes the dashboard so totals and rows reflect the new
  // visibility.
  const toggleShowHidden = async () => {
    if (hiddenToggling) return;
    const next = !showHidden;
    setHiddenToggling(true);
    try {
      await api.setShowHiddenData(next);
      setShowHidden(next);
      refresh();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not update demo/test visibility"
      );
    } finally {
      setHiddenToggling(false);
    }
  };

  const totals = data?.totals || {};
  const orgs = data?.orgs || [];
  const filteredOrgs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) =>
      [
        o.name,
        o.account_type,
        o.access_type,
        o.membership_status,
        o.billing_interval,
      ]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [orgs, query]);

  const drillTo = (path, orgId) => {
    navigate(`${path}?as_org=${encodeURIComponent(orgId)}`);
  };

  if (user?.permission_role !== "super_admin") {
    return (
      <div
        className="p-8 max-w-3xl mx-auto"
        data-testid="platform-dashboard-denied"
      >
        <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
          <CardContent className="p-8 text-center space-y-3">
            <ShieldCheck size={36} weight="duotone" className="mx-auto text-[#9C6B2E]" />
            <h2 className="text-lg font-semibold text-[#1F2937] heading-font">
              Platform Dashboard is super admin only
            </h2>
            <p className="text-sm text-[#6B7280]">
              You do not have access to this surface. Return to your
              workspace to continue.
            </p>
            <Link
              to="/"
              data-testid="platform-dashboard-denied-back"
              className="text-sm text-[#1E3F33] underline decoration-[#8FA998]/60 underline-offset-[3px]"
            >
              Back to workspace
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="p-6 max-w-7xl mx-auto"
      data-testid="platform-dashboard"
    >
      {/* Page header */}
      <header className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-[#1E3F33] flex items-center justify-center shrink-0">
            <ShieldCheck size={20} weight="duotone" className="text-white" />
          </div>
          <div className="min-w-0">
            <h1
              data-testid="platform-dashboard-title"
              className="text-2xl font-semibold text-[#1F2937] heading-font tracking-tight"
            >
              Platform Control Center
            </h1>
            <p className="text-[12px] text-[#6B7280] mt-1">
              Frame Practice — Super Admin view across every organization,
              school, user, and billing posture.
            </p>
          </div>
        </div>
        {/* FP3-037 correction — Top-level onboarding action bar.
            Three clear platform-owner actions surface above every
            other card so a new platform owner has an obvious path
            from "I just logged in" to "an org admin can accept their
            invite". */}
        <div
          className="flex flex-wrap items-center gap-2"
          data-testid="platform-action-bar"
        >
          <Button
            data-testid="platform-add-org-btn"
            onClick={() => setAddOrgOpen(true)}
            className="bg-[#1F4F73] hover:bg-[#143D5B] text-white"
          >
            <Plus size={14} weight="bold" className="mr-1.5" /> Add Organization
          </Button>
          <Button
            data-testid="platform-add-school-btn"
            onClick={() => {
              setAddSchoolOrgId("");
              setAddSchoolOpen(true);
            }}
            variant="outline"
            className="border-[#1F4F73] text-[#1F4F73] hover:bg-[#E6F1F8]"
          >
            <Buildings size={14} weight="duotone" className="mr-1.5" /> Add School / Campus
          </Button>
          <Button
            data-testid="platform-pilot-setup-btn"
            onClick={() => navigate("/platform/pilot-setup")}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            <Sparkle size={14} weight="duotone" className="mr-1.5" /> New Pilot Org Setup
          </Button>
          <Button
            data-testid="platform-invite-admin-btn"
            onClick={() => {
              setInviteOrgId("");
              setInviteOpen(true);
            }}
            variant="outline"
            className="border-[#1F4F73] text-[#1F4F73] hover:bg-[#E6F1F8]"
          >
            <UserPlus size={14} weight="duotone" className="mr-1.5" /> Invite Org Admin
          </Button>
          {/* FP3-043 Phase C — Show / hide demo + test data. Stored
              server-side on the super admin's user row so it
              persists across sessions and devices. */}
          <Button
            data-testid="platform-show-hidden-toggle"
            onClick={toggleShowHidden}
            disabled={hiddenToggling}
            variant="outline"
            className={
              showHidden
                ? "border-[#9C6B2E] bg-[#FBF3E4] text-[#9C6B2E] hover:bg-[#F4E6CD]"
                : "border-[#E5E3DB] text-[#6B7280] hover:bg-[#F8F7F2]"
            }
            title={
              showHidden
                ? "Currently showing demo / test / archived rows. Click to hide."
                : "Currently hiding demo / test / archived rows. Click to show."
            }
          >
            {showHidden ? (
              <Eye size={14} weight="duotone" className="mr-1.5" />
            ) : (
              <EyeSlash size={14} weight="duotone" className="mr-1.5" />
            )}
            {showHidden ? "Showing demo/test" : "Hide demo/test"}
          </Button>
          <span
            className="text-[11px] text-[#9AA0A6] ml-1 hidden lg:inline"
            data-testid="platform-dashboard-generated"
          >
            {data?.generated_at
              ? `Refreshed ${new Date(data.generated_at).toLocaleTimeString()}`
              : ""}
          </span>
        </div>
      </header>

      {/* FP3-037 correction — Stripe / billing readiness banner. Sits
          between the header and totals so the platform owner cannot
          miss it. Stripe is NOT integrated; we say so plainly. */}
      <section data-testid="platform-stripe-banner" className="mb-6">
        <Card className="border border-[#D7DFE6] bg-[#F2F6F9] rounded-2xl">
          <CardContent className="p-4 flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-[#1F4F73] flex items-center justify-center shrink-0">
                <CreditCard size={18} weight="duotone" className="text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-[#0F3554] heading-font">
                  Billing: Manual &middot; Stripe: Not connected
                </p>
                <p className="text-[12px] text-[#1F4F73] mt-0.5">
                  Stripe Billing v1 has not shipped. Pilot orgs are billed
                  manually. Existing organization records carry placeholder
                  <code className="mx-1 px-1 py-0.5 bg-white/70 rounded text-[10.5px] text-[#143D5B]">stripe_customer_id</code>
                  and
                  <code className="mx-1 px-1 py-0.5 bg-white/70 rounded text-[10.5px] text-[#143D5B]">stripe_subscription_id</code>
                  fields but neither is populated by the app.
                </p>
              </div>
            </div>
            <Button
              data-testid="platform-stripe-checklist-toggle"
              variant="outline"
              size="sm"
              onClick={() => setStripeChecklistOpen((v) => !v)}
              className="border-[#1F4F73] text-[#1F4F73] hover:bg-white"
            >
              <Info size={14} weight="duotone" className="mr-1.5" />
              {stripeChecklistOpen ? "Hide checklist" : "What's needed for v1?"}
            </Button>
          </CardContent>
          {stripeChecklistOpen && (
            <CardContent
              data-testid="platform-stripe-checklist"
              className="px-4 pb-4 pt-0"
            >
              <div className="rounded-lg bg-white border border-[#D7DFE6] p-4 text-[12px] text-[#3F4756] space-y-2">
                <p className="font-semibold text-[#0F3554]">
                  Stripe Billing v1 — checklist
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Create Stripe products + prices for each plan (pilot, membership, etc.).</li>
                  <li>Set environment variables: <code>STRIPE_SECRET_KEY</code>, <code>STRIPE_WEBHOOK_SECRET</code>, <code>STRIPE_PUBLISHABLE_KEY</code>, price IDs.</li>
                  <li>Implement <code>POST /api/billing/checkout</code> — checkout session creation.</li>
                  <li>Implement <code>POST /api/billing/portal</code> — customer portal creation.</li>
                  <li>Implement <code>POST /api/billing/webhook</code> with Stripe-Signature verification + idempotency store.</li>
                  <li>Wire event handlers: <code>checkout.session.completed</code>, <code>customer.subscription.created/updated/deleted</code>, <code>invoice.payment_succeeded/failed</code>.</li>
                  <li>Sync results back to <code>organizations</code>: store stripe_customer_id, stripe_subscription_id, sync membership_status / trial_ends_at.</li>
                  <li>Surface Subscribe / Manage Subscription buttons on the Org Admin Billing tab.</li>
                </ul>
                <p className="text-[11px] text-[#6B7280] pt-2 border-t border-[#F1F2F4]">
                  Full audit + estimate lives at <code>memory/FP3-037_STRIPE_RESEND_AUDIT.md</code>.
                </p>
              </div>
            </CardContent>
          )}
        </Card>
      </section>

      {/* Tier 1 — Totals */}
      <section className="mb-3" data-testid="platform-totals">
        <div className="flex items-baseline justify-between gap-2 mb-2">
          <h2 className="text-[11px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
            {/* FP3-053 Follow-up #3 — Make it explicit that the
                cards reflect the visible/operational pilot view
                (rows attached to hidden demo/test or to orgs not
                on the table below are excluded). */}
            Operational totals · pilot view
          </h2>
          <p className="text-[10.5px] text-[#9AA0A6]">
            Matches the visible organization table below
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat
            label="Visible organizations"
            value={loading ? "…" : totals.organizations}
            testid="stat-organizations"
            icon={Buildings}
          />
          <Stat
            label="Visible schools"
            value={loading ? "…" : totals.schools_total}
            testid="stat-schools-total"
            icon={Buildings}
          />
          <Stat
            label="Visible users"
            value={loading ? "…" : totals.users_total}
            testid="stat-users-total"
            icon={Users}
          />
          <Stat
            label="Visible admins"
            value={loading ? "…" : totals.users_admin}
            testid="stat-users-admin"
            icon={ShieldCheck}
          />
          <Stat
            label="Visible providers"
            value={loading ? "…" : totals.users_provider}
            testid="stat-users-provider"
            icon={Users}
          />
          <Stat
            label="Visible students"
            value={loading ? "…" : totals.students_total}
            testid="stat-students-total"
            icon={GraduationCap}
          />
        </div>
      </section>

      {/* FP3-053 Follow-up #3 — Diagnostics strip. Lives under the
          totals as a low-key one-liner so the operator can spot
          stale demo/test rows without the dashboard becoming
          cluttered. Renders only when there's something to report
          (i.e. ANY hidden_* counter > 0) so the calm pilot view
          stays calm when the DB is clean. */}
      {data?.diagnostics && (() => {
        const d = data.diagnostics;
        const hiddenTotal =
          (d.hidden_schools || 0) +
          (d.hidden_users || 0) +
          (d.hidden_students || 0) +
          (d.hidden_invites_pending || 0) +
          (d.hidden_access_pending || 0);
        if (hiddenTotal === 0) return null;
        return (
          <section
            className="mb-6"
            data-testid="platform-totals-diagnostics"
          >
            <div className="rounded-lg border border-[#E5E3DB] bg-[#FBFAF6] px-3 py-2 text-[11.5px] text-[#6B7280] flex items-start gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-[#9C6B2E] shrink-0">
                Diagnostics
              </span>
              <span className="text-[#4B5563]">
                Hidden from pilot view —
              </span>
              {d.hidden_schools > 0 && (
                <span className="text-[#4B5563]">
                  <strong className="text-[#1F2937]">{d.hidden_schools}</strong>{" "}
                  schools
                </span>
              )}
              {d.hidden_users > 0 && (
                <span className="text-[#4B5563]">
                  · <strong className="text-[#1F2937]">{d.hidden_users}</strong>{" "}
                  users
                </span>
              )}
              {d.hidden_students > 0 && (
                <span className="text-[#4B5563]">
                  · <strong className="text-[#1F2937]">{d.hidden_students}</strong>{" "}
                  students
                </span>
              )}
              {d.hidden_invites_pending > 0 && (
                <span className="text-[#4B5563]">
                  ·{" "}
                  <strong className="text-[#1F2937]">
                    {d.hidden_invites_pending}
                  </strong>{" "}
                  pending invites
                </span>
              )}
              {d.hidden_access_pending > 0 && (
                <span className="text-[#4B5563]">
                  ·{" "}
                  <strong className="text-[#1F2937]">
                    {d.hidden_access_pending}
                  </strong>{" "}
                  access requests
                </span>
              )}
              <span className="text-[#9AA0A6] ml-1">
                (toggle <em>Showing demo/test</em> above to reveal)
              </span>
            </div>
          </section>
        );
      })()}

      {/* Tier 2 — Pending + School state + Billing rollup */}
      <section className="mb-6" data-testid="platform-readiness">
        <h2 className="text-[11px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold mb-2">
          Access, invites &amp; billing readiness · pilot view
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat
            label="Active pending invites"
            value={loading ? "…" : totals.invites_pending}
            testid="stat-invites-pending"
            icon={EnvelopeSimple}
            tone="amber"
          />
          <Stat
            label="Active pending access requests"
            value={loading ? "…" : totals.access_requests_pending}
            testid="stat-access-requests-pending"
            icon={EnvelopeSimple}
            tone="amber"
          />
          <Stat
            label="Schools · active"
            value={loading ? "…" : totals.schools_active}
            testid="stat-schools-active"
            icon={Buildings}
            tone="green"
          />
          <Stat
            label="Schools · trial"
            value={loading ? "…" : totals.schools_trial}
            testid="stat-schools-trial"
            icon={Buildings}
            tone="blue"
          />
          <Stat
            label="Stripe customers linked"
            value={loading ? "…" : totals.orgs_with_stripe_customer}
            testid="stat-orgs-with-stripe-customer"
            icon={CreditCard}
            tone="blue"
          />
          <Stat
            label="Membership active"
            value={loading ? "…" : totals.orgs_membership_active}
            testid="stat-orgs-membership-active"
            icon={CreditCard}
            tone="green"
          />
        </div>
      </section>

      {/* Tier 3 — Organizations table */}
      <section data-testid="platform-orgs-section">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h2 className="text-[11px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
            Organizations · {filteredOrgs.length} of {orgs.length}
          </h2>
          <div className="relative w-full sm:w-64">
            <MagnifyingGlass
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9AA0A6]"
            />
            <Input
              data-testid="platform-orgs-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search org name, access, plan…"
              className="pl-7 h-9 text-[12.5px] bg-white border-[#E5E3DB] focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
            />
          </div>
        </div>

        <Card className="border border-[#E5E3DB] rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            {loading && (
              <div
                className="p-8 text-center text-sm text-[#6B7280]"
                data-testid="platform-orgs-loading"
              >
                Loading organizations…
              </div>
            )}
            {!loading && filteredOrgs.length === 0 && (
              <div
                className="p-8 text-center text-sm text-[#6B7280]"
                data-testid="platform-orgs-empty"
              >
                No organizations match your search.
              </div>
            )}
            {!loading && filteredOrgs.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]" data-testid="platform-orgs-table">
                  <thead className="bg-[#FBFAF6] text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">
                    <tr>
                      <th className="text-left px-4 py-2.5">Organization</th>
                      <th className="text-left px-3 py-2.5">Access</th>
                      <th className="text-left px-3 py-2.5">Membership</th>
                      <th className="text-left px-3 py-2.5">Billing</th>
                      <th className="text-left px-3 py-2.5">Stripe</th>
                      <th className="text-left px-3 py-2.5">Schools</th>
                      <th className="text-left px-3 py-2.5">Admins</th>
                      <th className="text-left px-3 py-2.5">Providers</th>
                      <th className="text-left px-3 py-2.5">Students</th>
                      <th className="text-left px-3 py-2.5">Created</th>
                      <th className="text-right px-4 py-2.5">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F1F2F4]">
                    {filteredOrgs.map((o) => (
                      <tr
                        key={o.id}
                        data-testid={`platform-org-row-${o.id}`}
                        className="hover:bg-[#FBFAF6] transition-colors"
                      >
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-1.5 max-w-[260px]">
                            {/* FP3-074 — Org name is the primary click
                                target. Opens the per-org Console where
                                full details + the action panel live. */}
                            <button
                              type="button"
                              data-testid={`platform-row-open-console-${o.id}`}
                              onClick={() => navigate(`/platform/org/${encodeURIComponent(o.id)}`)}
                              className="font-medium text-[#1E3F33] truncate text-left hover:underline decoration-[#8FA998]/70 underline-offset-[3px] decoration-1"
                              title="Open Organization Console"
                            >
                              {o.name || "—"}
                            </button>
                            <ClassificationBadge
                              row={o}
                              testid={`platform-org-classification-${o.id}`}
                            />
                          </div>
                          <div className="text-[10.5px] text-[#9AA0A6] mt-0.5">
                            {o.account_type || "—"}
                          </div>
                        </td>
                        <td className="px-3 py-2.5">{chip(o.access_type)}</td>
                        <td className="px-3 py-2.5">{chip(o.membership_status)}</td>
                        <td className="px-3 py-2.5">{chip(o.billing_interval)}</td>
                        <td className="px-3 py-2.5">
                          <span
                            className="inline-flex items-center gap-1 text-[10.5px] text-[#6B7280]"
                            data-testid={`platform-org-stripe-${o.id}`}
                            title={
                              o.has_stripe_subscription
                                ? "Subscription linked (placeholder ID)"
                                : o.has_stripe_customer
                                  ? "Customer linked (placeholder ID) · no subscription"
                                  : "Stripe not connected"
                            }
                          >
                            <CreditCard size={12} weight="duotone" />
                            {o.has_stripe_subscription
                              ? "Subscribed"
                              : o.has_stripe_customer
                                ? "Customer"
                                : "Not connected"}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-[#1F2937]">{o.school_count}</td>
                        <td
                          data-testid={`platform-org-admin-count-${o.id}`}
                          className="px-3 py-2.5"
                        >
                          <button
                            type="button"
                            data-testid={`platform-row-view-admins-${o.id}`}
                            onClick={() => openAdminsModal(o)}
                            className="text-[12.5px] text-[#1E3F33] font-semibold underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors"
                            title="View admin contacts for this organization"
                          >
                            {o.user_admin_count}
                          </button>
                        </td>
                        <td className="px-3 py-2.5 text-[#1F2937]">{o.user_provider_count}</td>
                        <td className="px-3 py-2.5 text-[#1F2937]">{o.student_count}</td>
                        <td className="px-3 py-2.5 text-[10.5px] text-[#6B7280] whitespace-nowrap">
                          {o.created_at ? o.created_at.slice(0, 10) : "—"}
                        </td>
                        <td className="px-4 py-2.5">
                          {/* FP3-074 — Row actions trimmed. Org name is
                              the primary click target; secondary
                              actions live on the Org Console action
                              panel. We keep only the three highest-
                              frequency platform operations on the
                              table row: open Console, drill into the
                              org as admin, and add a school. */}
                          <div className="flex items-center justify-end gap-1.5">
                            <RowAction
                              testid={`platform-row-open-${o.id}`}
                              icon={ArrowRight}
                              label="Open Console"
                              onClick={() => navigate(`/platform/org/${encodeURIComponent(o.id)}`)}
                            />
                            <RowAction
                              testid={`platform-row-drill-${o.id}`}
                              icon={Eye}
                              label="Drill into org (Admin view)"
                              onClick={() => drillTo("/admin/dashboard", o.id)}
                            />
                            <RowAction
                              testid={`platform-row-add-school-${o.id}`}
                              icon={Plus}
                              label="Add school to this org"
                              onClick={() => {
                                setAddSchoolOrgId(o.id);
                                setAddSchoolOpen(true);
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        <p className="text-[10.5px] text-[#9AA0A6] mt-2">
          Click an organization name to open its Console — main contacts,
          overview counts, and the full action panel live there. Org admins
          never see this dashboard.
        </p>
      </section>

      {/* FP3-037 correction — Onboarding modals. Each handles its
          own form state; we just give them the org list + a refresh
          callback so the dashboard updates immediately after a
          successful action. */}
      <AddOrganizationModal
        open={addOrgOpen}
        onClose={() => setAddOrgOpen(false)}
        onCreated={() => refresh()}
      />
      <AddSchoolModal
        open={addSchoolOpen}
        onClose={() => setAddSchoolOpen(false)}
        onCreated={() => refresh()}
        organizations={orgs}
        prefillOrganizationId={addSchoolOrgId}
      />
      <InviteUserModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCreated={() => refresh()}
        prefillOrganizationId={inviteOrgId}
        prefillIntendedRole="org_admin"
      />
      {/* FP3-049 — Org admins modal. Surfaces who administers a
          given organization so the Super Admin can see admin
          contacts separately from the provider roster. */}
      <OrgAdminsModal
        open={!!adminsModalOrg}
        org={adminsModalOrg}
        admins={adminsModalOrg ? adminsByOrg[adminsModalOrg.id] : null}
        loading={adminsLoading}
        onClose={() => setAdminsModalOrg(null)}
        onInvite={(orgId) => {
          setAdminsModalOrg(null);
          setInviteOrgId(orgId);
          setInviteOpen(true);
        }}
        onManageAccess={(orgId) => {
          setAdminsModalOrg(null);
          drillTo("/admin/access", orgId);
        }}
      />
    </div>
  );
}

// ── FP3-049 — Org admins modal ─────────────────────────────────────

function OrgAdminsModal({ open, org, admins, loading, onClose, onInvite, onManageAccess }) {
  if (!open || !org) return null;
  const rows = admins || [];
  return (
    <div
      data-testid="platform-org-admins-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#1F2937]/40 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl border border-[#E5E3DB] shadow-[0_18px_60px_rgba(31,41,55,0.18)] w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-3 border-b border-[#F1F2F4]">
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#1E3F33] font-semibold">
            Organization admins
          </div>
          <div className="mt-1 text-[18px] font-semibold text-[#1F2937] heading-font leading-tight">
            {org.name || "Organization"}
          </div>
          <div className="mt-1 text-[12px] text-[#6B7280]">
            {org.account_type || "—"} · {org.user_admin_count} admin
            {org.user_admin_count === 1 ? "" : "s"}
          </div>
        </div>

        <div className="px-6 py-4 max-h-[420px] overflow-y-auto">
          {loading && (
            <div className="text-[12.5px] text-[#6B7280]">Loading admins…</div>
          )}
          {!loading && rows.length === 0 && (
            <div
              data-testid="platform-org-admins-empty"
              className="text-[12.5px] text-[#6B7280] bg-[#F8F7F2] border border-[#E5E3DB] rounded-md px-3 py-3"
            >
              No administrators listed for this organization yet. Invite an
              Org Admin to get this organization staffed.
            </div>
          )}
          {!loading && rows.length > 0 && (
            <ul className="space-y-2.5">
              {rows.map((a) => (
                <li
                  key={a.id}
                  data-testid={`platform-org-admin-${a.id}`}
                  className="flex items-start justify-between gap-3 py-2 border-b border-[#F1F2F4] last:border-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] text-[#1F2937] font-medium truncate">
                      {(`${a.first_name || ""} ${a.last_name || ""}`).trim() || a.email}
                    </div>
                    <div className="text-[11px] text-[#6B7280] uppercase tracking-[0.08em]">
                      {(a.permission_role || "").replace("_", " ") || "—"}
                    </div>
                    <a
                      href={`mailto:${a.email}`}
                      className="mt-1 inline-flex items-center gap-1 text-[12px] text-[#1E3F33] font-medium hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors"
                    >
                      <EnvelopeSimple size={12} weight="duotone" />
                      {a.email}
                    </a>
                  </div>
                  <span
                    className="flex-shrink-0 text-[10.5px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-[0.08em] bg-[#E8F0EB] text-[#1E3F33]"
                  >
                    {a.account_status || "active"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-6 py-4 bg-[#FBFAF6] border-t border-[#EDEAE0] flex items-center justify-between gap-2">
          <Button
            data-testid="platform-org-admins-modal-invite"
            variant="outline"
            onClick={() => onInvite(org.id)}
            className="h-9 px-3 text-[12.5px] border-[#1E3F33] text-[#1E3F33] hover:bg-[#E8F0EB]"
          >
            Invite an admin
          </Button>
          <div className="flex items-center gap-2">
            <Button
              data-testid="platform-org-admins-modal-manage"
              variant="outline"
              onClick={() => onManageAccess(org.id)}
              className="h-9 px-3 text-[12.5px] border-[#E5E3DB] text-[#1F2937] hover:bg-[#F8F7F2]"
            >
              Manage access
            </Button>
            <Button
              data-testid="platform-org-admins-modal-close"
              onClick={onClose}
              className="h-9 px-3 text-[12.5px] bg-[#1E3F33] hover:bg-[#173127] text-white"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RowAction({ testid, icon: Icon, label, onClick }) {
  return (
    <Button
      type="button"
      data-testid={testid}
      onClick={onClick}
      variant="outline"
      size="sm"
      title={label}
      className="h-7 w-7 p-0 border-[#E5E3DB] text-[#3F4756] hover:bg-[#F1F2F4]"
      aria-label={label}
    >
      <Icon size={13} weight="duotone" />
    </Button>
  );
}
